/**
 * Default tab chart tooltips (OTD, OTIF, PPM).
 *
 * Moved out of DefaultTab.tsx under CR-7776 to keep that module under the
 * 500-line limit. Bodies are unchanged.
 */
import { useFormatters } from '../../contexts/PreferencesContext'
import { formatChartPercent } from './chartHeadline'

/**
 * CR-4672: Custom tooltip for the OTD chart.
 * Reorders metrics to Early → Late → On-Time → OTD % → Target % → Trend %
 * and formats the three percentage series to the company's KPI decimal setting
 * (CR-6155; it was a fixed 2 decimals) + "%" suffix. Count-based series
 * (Early / Late / On-Time) render as plain integers.
 *
 * CR-4673: The OTIF tooltip uses the same shape with its own ordering and
 * percentage set (see OTIFChartTooltip below).
 */
type ChartTooltipItem = {
  dataKey?: string
  name?: string
  value?: number | string | null
  color?: string
  payload?: Record<string, number | string | null | undefined>
}

function renderOrderedTooltip(
  active: boolean | undefined,
  payload: ChartTooltipItem[] | undefined,
  label: string | undefined,
  order: string[],
  displayNames: Record<string, string>,
  percentKeys: Set<string>,
  decimals: number,
) {
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const rows = order
    .map((key) => ({ key, item: byKey[key] }))
    .filter((row) => row.item !== undefined)

  const formatValue = (key: string, value: number | string | null | undefined): string => {
    if (value == null || value === '') return '-'
    const num = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(num)) return '-'
    if (percentKeys.has(key)) return formatChartPercent(num, decimals)
    return String(Math.round(num))
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{
        margin: 0,
        padding: 10,
        backgroundColor: 'rgb(255, 255, 255)',
        border: '1px solid rgb(204, 204, 204)',
        whiteSpace: 'nowrap',
      }}
    >
      {label && (
        <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>
      )}
      <ul
        className="recharts-tooltip-item-list"
        style={{ padding: 0, margin: 0, listStyle: 'none' }}
      >
        {rows.map(({ key, item }) => (
          <li
            key={key}
            className="recharts-tooltip-item"
            style={{
              display: 'block',
              paddingTop: 4,
              paddingBottom: 4,
              color: item!.color,
            }}
          >
            <span className="recharts-tooltip-item-name">{displayNames[key]}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">
              {formatValue(key, item!.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function OTDChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  const { preferences } = useFormatters()
  const order = ['early', 'late', 'onTime', 'otd', 'target', 'otdTrend']
  const displayNames: Record<string, string> = {
    early: 'Early',
    late: 'Late',
    onTime: 'On-Time',
    otd: 'OTD %',
    target: 'Target %',
    otdTrend: 'Trend %',
  }
  const percentKeys = new Set(['otd', 'target', 'otdTrend'])
  return renderOrderedTooltip(
    active, payload, label, order, displayNames, percentKeys, preferences.kpi_percent_decimals ?? 0,
  )
}

/**
 * CR-4673 / CR-4668 / CR-4709: Custom tooltip for the OTIF chart.
 * Shows count breakdown (Passed, Failed, Arrears, Total) then percentages.
 * Reads `totalLines` and `otif` from the backend payload directly so the
 * tooltip, the chart line and the drill-down all show the same numbers.
 */
export function OTIFChartTooltip({ active, payload, label }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string }) {
  // CR-6155: percentages follow the KPI decimal setting (was a fixed 2).
  const { preferences } = useFormatters()
  const decimals = preferences.kpi_percent_decimals ?? 0
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const toNum = (val: number | string | null | undefined): number => {
    if (val == null || val === '') return 0
    const n = typeof val === 'number' ? val : Number(val)
    return Number.isFinite(n) ? n : 0
  }

  const row = payload[0]?.payload
  const otifCount = toNum(byKey['otifCount']?.value)
  const failed = toNum(byKey['failed']?.value)
  const arrears = toNum(byKey['arrears']?.value)
  // CR-4709 round 6: backend now ships totalLines (Count('id') over every PO
  // line in the month, matching the drill-down's TOTAL LINES tab). Fall back
  // to passed+failed+arrears only if the field is missing on older data.
  const total = toNum(row?.['totalLines']) || (otifCount + failed + arrears)
  const otifFromBackend = row?.['otif']
  const otif = otifFromBackend != null && otifFromBackend !== '' ? toNum(otifFromBackend) : null
  const target = toNum(byKey['target']?.value)
  const trend = byKey['otifTrend']?.value

  type Row = { label: string; value: string; color?: string }
  const rows: Row[] = [
    { label: 'Passed OTIF PO Lines', value: String(Math.round(otifCount)), color: byKey['otifCount']?.color },
    { label: 'Failed OTIF PO Lines', value: String(Math.round(failed)), color: byKey['failed']?.color },
    { label: 'Arrears PO Lines', value: String(Math.round(arrears)), color: byKey['arrears']?.color },
    { label: 'Total PO Lines', value: String(Math.round(total)), color: '#6b7280' },
    { label: 'OTIF %', value: otif !== null ? formatChartPercent(otif, decimals) : '-', color: byKey['otif']?.color },
    { label: 'Target %', value: formatChartPercent(target, decimals), color: byKey['target']?.color },
  ]
  if (trend != null && trend !== '') {
    rows.push({ label: 'Trend %', value: formatChartPercent(toNum(trend), decimals), color: byKey['otifTrend']?.color })
  }

  return (
    <div
      className="recharts-default-tooltip"
      style={{ margin: 0, padding: 10, backgroundColor: 'rgb(255, 255, 255)', border: '1px solid rgb(204, 204, 204)', whiteSpace: 'nowrap' }}
    >
      {label && <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>}
      <ul className="recharts-tooltip-item-list" style={{ padding: 0, margin: 0, listStyle: 'none' }}>
        {rows.map((row) => (
          <li key={row.label} className="recharts-tooltip-item" style={{ display: 'block', paddingTop: 4, paddingBottom: 4, color: row.color }}>
            <span className="recharts-tooltip-item-name">{row.label}</span>
            <span className="recharts-tooltip-item-separator"> : </span>
            <span className="recharts-tooltip-item-value">{row.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * CR-4674: Custom tooltip for the PPM chart.
 * Rounds values to integers with comma formatting (e.g. 79,985 not 79984.5)
 * and uses the configured quality measure label ("PPM" / "PPK").
 */
export function PPMChartTooltip({ active, payload, label, measureLabel }: { active?: boolean; payload?: ChartTooltipItem[]; label?: string; measureLabel: string }) {
  const order = ['ppm', 'target', 'ppmTrend']
  const displayNames: Record<string, string> = {
    ppm: measureLabel,
    target: 'Target',
    ppmTrend: 'Trend',
  }
  if (!active || !payload || payload.length === 0) return null

  const byKey: Record<string, ChartTooltipItem> = {}
  for (const item of payload) {
    if (item.dataKey) byKey[String(item.dataKey)] = item
  }

  const rows = order
    .map((key) => ({ key, item: byKey[key] }))
    .filter((row) => row.item !== undefined)

  return (
    <div
      className="recharts-default-tooltip"
      style={{
        margin: 0,
        padding: 10,
        backgroundColor: 'rgb(255, 255, 255)',
        border: '1px solid rgb(204, 204, 204)',
        whiteSpace: 'nowrap',
      }}
    >
      {label && (
        <p className="recharts-tooltip-label" style={{ margin: 0 }}>{label}</p>
      )}
      <ul
        className="recharts-tooltip-item-list"
        style={{ padding: 0, margin: 0, listStyle: 'none' }}
      >
        {rows.map(({ key, item }) => {
          const rawValue = item!.value
          const num = typeof rawValue === 'number' ? rawValue : Number(rawValue)
          const formatted = Number.isFinite(num) ? Math.round(num).toLocaleString() : '-'
          return (
            <li
              key={key}
              className="recharts-tooltip-item"
              style={{
                display: 'block',
                paddingTop: 4,
                paddingBottom: 4,
                color: item!.color,
              }}
            >
              <span className="recharts-tooltip-item-name">{displayNames[key]}</span>
              <span className="recharts-tooltip-item-separator"> : </span>
              <span className="recharts-tooltip-item-value">{formatted}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
